<template>
    <div class="border-grey q-pa-md">
        <v-chart :option="options" class="graph" autoresize />
    </div>
</template>

<script>
import { LinesChart } from 'echarts/charts';
import {
    DataZoomComponent,
    GridComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { colors, date } from 'quasar';
import VChart from 'vue-echarts';
const { getPaletteColor } = colors;
import { sanitizeString } from '@utils/sanitize.js';

use([
    CanvasRenderer,
    LinesChart,
    TooltipComponent,
    GridComponent,
    TitleComponent,
    DataZoomComponent
]);

export default {
    name: 'TrendsReportGraph',
    components: { VChart },
    props: {
        series: {
            type: Object,
            required: true
        },
        reportResolution: {
            type: String,
            required: true
        }
    },
    computed: {
        options() {
            return {
                grid: {
                    left: '3%',
                    right: '4%',
                    top: this.$q.screen.gt.sm ? '15%' : '5%',
                    bottom: '10%',
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },
                animation: false,
                title: {
                    show: this.$q.screen.gt.sm,
                    text: this.$t('OEE Metrics')
                },
                tooltip: {
                    trigger: 'axis',
                    formatter: params => this.getTooltipFormatter(params)
                },
                legend: {
                    bottom: 0,
                    data: [
                        this.$t('OEE'),
                        this.$t('Availability'),
                        this.$t('Performance'),
                        this.$t('Quality')
                    ]
                },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    offset: 10,
                    data: Array.from(this.series.periods),
                    axisLabel: {
                        formatter: value => {
                            const label = this.formatDate(value);
                            // break the label into two lines on the blank space
                            const index = label.indexOf(' ');
                            if (index !== -1) {
                                const firstLine = label.substring(0, index);
                                const secondLine = label.substring(index + 1);
                                return `${firstLine}\n${secondLine}`;
                            }
                            return label;
                        }
                    },
                    axisTick: {
                        length: 15
                    }
                },
                yAxis: {
                    type: 'value',
                    axisLabel: {
                        formatter: value => {
                            return `${value * 100}%`;
                        }
                    }
                },
                series: [
                    {
                        name: this.$t('OEE'),
                        type: 'line',
                        data: this.series.oee,
                        color: getPaletteColor('medium-blue'),
                        z: 4
                    },
                    {
                        name: this.$t('Availability'),
                        type: 'line',
                        data: this.series.availability,
                        color: getPaletteColor('warning'),
                        z: 3
                    },
                    {
                        name: this.$t('Performance'),
                        type: 'line',
                        data: this.series.performance,
                        color: getPaletteColor('light-red'),
                        z: 2
                    },
                    {
                        name: this.$t('Quality'),
                        type: 'line',
                        data: this.series.quality,
                        color: getPaletteColor('light-green'),
                        z: 1
                    }
                ]
            };
        }
    },
    methods: {
        /**
         * Gets the tooltip content.
         * @param {*} params
         * @returns {string} The tooltip content.
         */
        getTooltipFormatter(params) {
            if (params.length === 0) {
                return `${this.$t('No data available')}`;
            }
            const date = params[0]?.axisValue;

            let text = '';

            const dateTitle = `${this.$t('Date')}:`;

            if (date) {
                const safeDate = sanitizeString(this.formatDate(date));
                text += `
                    <div>
                        ${dateTitle}
                        <span class="text-bold">
                            ${safeDate}
                        </span>
                    </div>
                `;
            }

            for (const param of params) {
                const name = sanitizeString(param.seriesName);
                const value = param.data;
                const marker = param.marker;
                const safeValue = sanitizeString(this.formatMetricValue(value));
                text += `
                    <div>
                        ${marker}
                        ${name}:
                        <span class="text-bold">
                            ${safeValue}
                        </span>
                    </div>
                `;
            }

            return `<div>${text}</div>`;
        },
        /**
         * Formats the value to a percentage with two decimal places.
         * @param {number} value The value to format.
         * @returns {string} The formatted value.
         */
        formatMetricValue(value) {
            if (value % 1 !== 0) {
                return `${(value * 100).toFixed(2)}%`;
            }
            return `${value * 100}%`;
        },
        /**
         * Formats the date to the specified format.
         * @param {string} value The date to format.
         * @returns {string} The formatted date.
         */
        formatDate(value) {
            if (
                this.reportResolution === 'hourly' ||
                this.reportResolution === '60-minute'
            ) {
                return date.formatDate(value, 'YYYY/MM/DD HH:mm');
            } else if (this.reportResolution === 'monthly') {
                return date.formatDate(value, 'YYYY/MM');
            }
            return date.formatDate(value, 'YYYY/MM/DD');
        }
    }
};
</script>

<style lang="scss" scoped>
.graph {
    width: 100%;
    height: 350px;
}
</style>
